// src/components/researchCalendarRooms.js
//
// Room → color helpers for the Research Calendar view page
// (ResearchCalendarPage.js), its event modal (CalendarEventModal.js) and
// PatientDetailPage's ScheduleVisitModal.
//
// Rooms used to be a hardcoded list living here, again in ScheduleVisitModal,
// and a third time in appsscript/research-calendar.gs — so "Room 1..4" was
// baked into the product and a new site couldn't have its own. They're now
// per-site data (see services/calendarConfigService.js); this file holds the
// COLOR/LABEL helpers and the `useCalendarRooms` hook that loads them.
//
// RegX renders its own event blocks (it doesn't embed the Google Calendar UI),
// so `fill`/`accent` are chosen for readability inside RegX and don't need to
// match Google's own event color — `colorId` is what gets written onto the
// actual Google Calendar event.
//
// Flat mid-light fill, tinted-badge style: text and the left border both use
// `accent` — a deep, saturated shade of the SAME hue as the fill (not plain
// black), which is the standard "tinted badge" contrast pattern and reads
// far easier than dark ink dropped on top of a colored background.

import { useEffect, useState } from "react";
import {
  DEFAULT_CALENDAR_ROOMS,
  fetchCalendarConfig,
} from "../services/calendarConfigService";

// Back-compat export: the default room set. Prefer `useCalendarRooms()` in
// components so a site's own rooms are picked up.
export const ROOMS = DEFAULT_CALENDAR_ROOMS;

const byName = (rooms) =>
  rooms.reduce((acc, r) => {
    acc[r.name] = r;
    return acc;
  }, {});

// Build the lookup helpers for a given room list. Anything not in the list
// (e.g. a room renamed after events were already booked under the old name)
// falls back to the "Other" styling rather than rendering unstyled.
export const makeRoomHelpers = (rooms = DEFAULT_CALENDAR_ROOMS) => {
  const list = Array.isArray(rooms) && rooms.length ? rooms : DEFAULT_CALENDAR_ROOMS;
  const map = byName(list);
  const fallback = map.Other || list[list.length - 1];
  return {
    rooms: list,
    roomNames: list.map((r) => r.name),
    roomFill: (roomName) => (map[roomName] || fallback).fill,
    roomAccent: (roomName) => (map[roomName] || fallback).accent,
    roomLabel: (roomName, otherRoom) =>
      roomName === "Other" && otherRoom ? otherRoom : roomName || "Room",
  };
};

const defaultHelpers = makeRoomHelpers(DEFAULT_CALENDAR_ROOMS);

// Back-compat standalone helpers, bound to the DEFAULT rooms. Existing imports
// keep working exactly as before.
export const ROOM_BY_NAME = byName(DEFAULT_CALENDAR_ROOMS);
export const roomFill = defaultHelpers.roomFill;
export const roomAccent = defaultHelpers.roomAccent;
export const roomLabel = defaultHelpers.roomLabel;

/**
 * Load the ACTIVE site's rooms.
 *
 * Returns the defaults synchronously on first render and swaps in the site's
 * own rooms once the config doc resolves, so nothing flashes empty and a site
 * with no config doc simply keeps the original Dubois room list.
 */
export const useCalendarRooms = () => {
  const [helpers, setHelpers] = useState(defaultHelpers);
  const [timeZone, setTimeZone] = useState("America/New_York");
  const [provider, setProvider] = useState("apps-script");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchCalendarConfig()
      .then((cfg) => {
        if (!alive) return;
        setHelpers(makeRoomHelpers(cfg.rooms));
        setTimeZone(cfg.timeZone);
        setProvider(cfg.provider);
      })
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, []);

  // `connected` is only trustworthy once `loaded` is true — assume connected
  // beforehand so the calendar doesn't flash a "not set up" message at a site
  // that is perfectly well set up.
  return { ...helpers, timeZone, provider, loaded, connected: provider !== "none" };
};
